'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { trackSignupStart, trackSignupSubmitted } from '@/lib/analytics';
import {
  Eye, EyeOff, Heart, Mail, Lock, User, Phone, AlertCircle,
  CheckCircle2, ArrowRight, BadgeCheck, FileText, CalendarCheck,
  Utensils, Shirt, Car, Home, Users, BookOpen, Briefcase, RefreshCw,
  Shield, Star, Sparkles,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const memberBenefits = [
  { icon: FileText, label: 'Personalized Impact Reports' },
  { icon: Mail, label: 'Exclusive Newsletters & Updates' },
  { icon: BadgeCheck, label: 'Digital Membership Badge' },
  { icon: CalendarCheck, label: 'Priority Event Access' },
];

const allowances = [
  { icon: Utensils, label: 'Food Support' },
  { icon: Shirt, label: 'Clothing Allowance' },
  { icon: Car, label: 'Transportation' },
  { icon: Home, label: 'Shelter Assistance' },
  { icon: Users, label: 'Social Damages' },
  { icon: BookOpen, label: 'Education Support' },
  { icon: Briefcase, label: 'Business Support' },
];

type Step = 'register' | 'verify' | 'activated';

export default function SignupPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [step, setStep] = useState<Step>('register');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackSignupStart();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await signUp(formData.email, formData.password, { fullName: formData.fullName });
      setRegisteredEmail(formData.email);
      trackSignupSubmitted();

      // Send verification email via Resend
      try {
        await fetch('/api/email/send-verification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email, name: formData.fullName }),
        });
      } catch {
        // Non-blocking — Supabase also sends its own verification email
        console.warn('[signup] Resend verification email failed silently');
      }

      setStep('verify');
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await fetch('/api/email/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail }),
      });
    } catch {
      // silent
    }
    setResendSent(true);
    setResendLoading(false);
    setTimeout(() => setResendSent(false), 4000);
  };

  const handleActivate = () => {
    setStep('activated');
    setTimeout(() => {
      router.push('/member-profile-page');
      router.refresh();
    }, 2000);
  };

  const inputClass =
    'w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-all focus:ring-2 focus:ring-[#D4A017]/40';
  const inputStyle = {
    background: '#1a1800',
    borderColor: '#2e2800',
    color: '#f5f0d0',
  } as React.CSSProperties;

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'linear-gradient(135deg, #0a0a00 0%, #1a1500 60%, #0a0a00 100%)' }}
    >
      {/* Left Panel — Benefits Sidebar (desktop only) */}
      <aside className="hidden xl:flex xl:w-[420px] flex-col justify-between p-10 relative overflow-hidden shrink-0">
        {/* Decorative glow */}
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 30% 20%, rgba(212,160,23,0.12) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(212,160,23,0.08) 0%, transparent 50%)',
          }}
        />
        <div className="relative">
          {/* Logo */}
          <Link href="/" className="inline-flex items-center gap-3 mb-12 group">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: 'linear-gradient(135deg, #D4A017, #A07810)' }}
            >
              <Heart size={24} className="text-black" />
            </div>
            <div>
              <p className="font-extrabold text-lg tracking-tight" style={{ color: '#D4A017' }}>
                MOURNDIECE
              </p>
              <p className="text-xs tracking-widest uppercase" style={{ color: '#a09050' }}>
                Foundation
              </p>
            </div>
          </Link>

          <h2 className="text-2xl font-bold mb-2" style={{ color: '#f5f0d0' }}>
            Why become a member?
          </h2>
          <p className="text-sm mb-8 leading-relaxed" style={{ color: '#a09050' }}>
            Join thousands of compassionate members transforming communities across Education, Health, and Environment.
          </p>

          {/* Benefits */}
          <div className="space-y-3 mb-8">
            {memberBenefits.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}
                >
                  <Icon size={15} style={{ color: '#D4A017' }} />
                </div>
                <span className="text-sm font-medium" style={{ color: '#e8d88a' }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Allowances */}
          <div
            className="rounded-2xl p-5 border"
            style={{ background: 'rgba(212,160,23,0.05)', borderColor: 'rgba(212,160,23,0.15)' }}
          >
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#D4A017' }}>
              Monthly Allowances
            </p>
            <div className="grid grid-cols-2 gap-2">
              {allowances.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon size={13} style={{ color: '#a09050' }} />
                  <span className="text-xs" style={{ color: '#a09050' }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust signals */}
        <div className="relative flex flex-col gap-2 mt-8">
          {[
            { icon: Shield, text: 'SSL Secured & Privacy Protected' },
            { icon: Star, text: 'Charity Navigator 4-Star Rated' },
            { icon: CheckCircle2, text: '100% Transparent Allocation' },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-2">
              <Icon size={13} style={{ color: '#a09050' }} />
              <span className="text-xs" style={{ color: '#a09050' }}>
                {text}
              </span>
            </div>
          ))}
        </div>
      </aside>

      {/* Right Panel — Form */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 lg:py-16">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="xl:hidden text-center mb-8">
            <Link href="/" className="inline-flex flex-col items-center gap-2">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, #D4A017, #A07810)' }}
              >
                <Heart size={24} className="text-black" />
              </div>
              <div>
                <p className="font-extrabold text-lg tracking-tight" style={{ color: '#D4A017' }}>
                  MOURNDIECE
                </p>
                <p className="text-xs tracking-widest uppercase" style={{ color: '#a09050' }}>
                  Foundation
                </p>
              </div>
            </Link>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {(['register', 'verify', 'activated'] as Step[]).map((s, i) => {
              const isActive = step === s;
              const isDone =
                (s === 'register' && (step === 'verify' || step === 'activated')) ||
                (s === 'verify' && step === 'activated');
              return (
                <React.Fragment key={s}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                    style={{
                      background: isDone
                        ? '#D4A017'
                        : isActive
                        ? 'linear-gradient(135deg, #D4A017, #A07810)'
                        : 'rgba(212,160,23,0.1)',
                      color: isDone || isActive ? '#0a0a00' : '#a09050',
                      border: isActive ? 'none' : isDone ? 'none' : '1px solid rgba(212,160,23,0.2)',
                    }}
                  >
                    {isDone ? <CheckCircle2 size={14} /> : i + 1}
                  </div>
                  {i < 2 && (
                    <div
                      className="h-px w-8 transition-all"
                      style={{ background: isDone ? '#D4A017' : 'rgba(212,160,23,0.2)' }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── STEP 1: Register ── */}
          {step === 'register' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#f5f0d0' }}>
                  Become a Member
                </h1>
                <p className="text-sm" style={{ color: '#a09050' }}>
                  Create your account and unlock your member dashboard
                </p>
              </div>

              <div
                className="rounded-2xl border p-7 shadow-2xl"
                style={{ background: '#111000', borderColor: '#2e2800' }}
              >
                {error && (
                  <div
                    className="mb-5 flex items-start gap-3 rounded-xl p-4 border"
                    style={{ background: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.25)' }}
                  >
                    <AlertCircle size={18} className="text-red-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: '#e8d88a' }}>
                      Full Name
                    </label>
                    <div className="relative">
                      <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#a09050' }} />
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: '#e8d88a' }}>
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#a09050' }} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: '#e8d88a' }}>
                      Phone{' '}
                      <span className="normal-case font-normal" style={{ color: '#a09050' }}>
                        (optional)
                      </span>
                    </label>
                    <div className="relative">
                      <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#a09050' }} />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Password row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: '#e8d88a' }}>
                        Password
                      </label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#a09050' }} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          placeholder="Min. 8 chars"
                          className={`${inputClass} pr-10`}
                          style={inputStyle}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                          style={{ color: '#a09050' }}
                        >
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: '#e8d88a' }}>
                        Confirm
                      </label>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#a09050' }} />
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                          placeholder="Re-enter"
                          className={`${inputClass} pr-10`}
                          style={inputStyle}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                          style={{ color: '#a09050' }}
                        >
                          {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed mt-2 flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #D4A017, #A07810)', color: '#0a0a00' }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Creating account…
                      </>
                    ) : (
                      <>
                        Create Member Account
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-5 text-center">
                  <p className="text-sm" style={{ color: '#a09050' }}>
                    Already a member?{' '}
                    <Link href="/login" className="font-semibold hover:underline" style={{ color: '#D4A017' }}>
                      Sign in
                    </Link>
                  </p>
                </div>
              </div>

              <p className="mt-5 text-center text-xs" style={{ color: '#a09050' }}>
                By registering, you agree to our{' '}
                <Link href="/" className="underline" style={{ color: '#D4A017' }}>
                  Terms
                </Link>{' '}
                and{' '}
                <Link href="/" className="underline" style={{ color: '#D4A017' }}>
                  Privacy Policy
                </Link>
              </p>
            </>
          )}

          {/* ── STEP 2: Email Verification ── */}
          {step === 'verify' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#f5f0d0' }}>
                  Verify Your Email
                </h1>
                <p className="text-sm" style={{ color: '#a09050' }}>
                  One step away from your member dashboard
                </p>
              </div>

              <div
                className="rounded-2xl border p-8 shadow-2xl text-center"
                style={{ background: '#111000', borderColor: '#2e2800' }}
              >
                {/* Animated mail icon */}
                <div
                  className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.15), rgba(212,160,23,0.05))', border: '1px solid rgba(212,160,23,0.25)' }}
                >
                  <Mail size={36} style={{ color: '#D4A017' }} />
                </div>

                <h2 className="text-lg font-bold mb-2" style={{ color: '#f5f0d0' }}>
                  Check your inbox
                </h2>
                <p className="text-sm mb-1" style={{ color: '#a09050' }}>
                  We sent a verification link to
                </p>
                <p className="text-sm font-semibold mb-6" style={{ color: '#D4A017' }}>
                  {registeredEmail}
                </p>

                <div
                  className="rounded-xl p-4 border mb-6 text-left"
                  style={{ background: 'rgba(212,160,23,0.05)', borderColor: 'rgba(212,160,23,0.15)' }}
                >
                  <p className="text-xs font-semibold mb-2" style={{ color: '#D4A017' }}>
                    What to do next:
                  </p>
                  <ol className="space-y-1.5">
                    {[
                      'Open the email from MOURNDIECE Foundation',
                      'Click the "Verify Email" button in the email',
                      'Return here and click "Activate Dashboard" below',
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs" style={{ color: '#a09050' }}>
                        <span
                          className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold"
                          style={{ background: 'rgba(212,160,23,0.2)', color: '#D4A017' }}
                        >
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                <button
                  onClick={handleActivate}
                  className="w-full py-3.5 rounded-xl font-bold text-sm mb-4 flex items-center justify-center gap-2 transition-all"
                  style={{ background: 'linear-gradient(135deg, #D4A017, #A07810)', color: '#0a0a00' }}
                >
                  <Sparkles size={16} />
                  Activate Member Dashboard
                </button>

                <button
                  onClick={handleResend}
                  disabled={resendLoading || resendSent}
                  className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                  style={{ color: '#a09050', border: '1px solid rgba(212,160,23,0.2)' }}
                >
                  {resendLoading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Sending…
                    </>
                  ) : resendSent ? (
                    <>
                      <CheckCircle2 size={14} style={{ color: '#D4A017' }} />
                      <span style={{ color: '#D4A017' }}>Email sent!</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      Resend verification email
                    </>
                  )}
                </button>

                <p className="mt-4 text-xs" style={{ color: '#a09050' }}>
                  Wrong email?{' '}
                  <button
                    onClick={() => setStep('register')}
                    className="underline font-medium"
                    style={{ color: '#D4A017' }}
                  >
                    Go back
                  </button>
                </p>
              </div>
            </>
          )}

          {/* ── STEP 3: Activated ── */}
          {step === 'activated' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#f5f0d0' }}>
                  Welcome to the Foundation!
                </h1>
                <p className="text-sm" style={{ color: '#a09050' }}>
                  Your membership is now active
                </p>
              </div>

              <div
                className="rounded-2xl border p-8 shadow-2xl text-center"
                style={{ background: '#111000', borderColor: '#2e2800' }}
              >
                <div
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #D4A017, #A07810)' }}
                >
                  <CheckCircle2 size={36} className="text-black" />
                </div>

                <h2 className="text-xl font-bold mb-2" style={{ color: '#f5f0d0' }}>
                  Account Activated!
                </h2>
                <p className="text-sm mb-6" style={{ color: '#a09050' }}>
                  Redirecting you to your member dashboard…
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {memberBenefits.map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2 p-3 rounded-xl border"
                      style={{ background: 'rgba(212,160,23,0.05)', borderColor: 'rgba(212,160,23,0.15)' }}
                    >
                      <Icon size={14} style={{ color: '#D4A017' }} />
                      <span className="text-xs" style={{ color: '#a09050' }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#D4A017" strokeWidth="4" />
                    <path className="opacity-75" fill="#D4A017" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-sm" style={{ color: '#a09050' }}>
                    Loading your dashboard…
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
